import { parseSanitizedHtml } from "@/utils/sanitizeHtml";
import { useCallback, useEffect, useMemo } from "react";
import Helpers from "@/Helpers.js";
import { FaRegCircle, FaCircle } from "react-icons/fa6";

export default function Motivators6(props) {
    const { sectionData, values, setValue } = props;
    const currentSection = sectionData?.currentSection;
    const registerSection = sectionData?.registerSection;
    const lang = sectionData?.lang || "eng";
    const content: Record<string, string> = Helpers.getSectionContent(currentSection, lang);
    const elements = Helpers.getSectionElements(content);

    const page = Number(sectionData?.page || 1);
    const allValues = useMemo(() => Array.isArray(elements?.items) ? elements.items : [], [elements]);
    const pageValues = allValues.filter((v) => Number(v?.page) === page);
    const instructionsHtml = Helpers.getSectionHtmlValue(content, "instructions");
    const reminderHtml = Helpers.getSectionHtmlValue(content, "reminder");

    function getBoxNumber(item) {
        return Number(item?.pagebox ?? item?.pageBox ?? item?.box ?? 0);
    }

    useEffect(() => {
        if (typeof registerSection !== "function") return;
        registerSection(400, allValues.length);
    }, [allValues.length, currentSection?.id, registerSection]);
    
    const getIssueCountForPage = useCallback(function (validationValues, page) {
        if (!Array.isArray(allValues)) {
            return 0; // Return 0 if values array doesn't exist
        }
        const pageValues = allValues.filter((m) => Number(m?.page) === Number(page));
        const requiredBoxes = [1, 2, 3, 4];

        const invalidBoxes = requiredBoxes.filter((boxNumber) => {
            const boxValues = pageValues.filter((m) => getBoxNumber(m) === boxNumber);
            if (boxValues.length === 0) return true;

            const selectedCount = boxValues.reduce((count, m) => {
                return count + (Number(validationValues?.[currentSection.formId + m.id] || 0) > 0 ? 1 : 0);
            }, 0);

            // A valid box must have at least one selection.
            return selectedCount < 1;
        });

        return invalidBoxes.length;
    }, [allValues, currentSection?.formId]);

    useEffect(() => {
        const nextCount = Number(sectionData?.nextCount || 0);
        const page = Number(sectionData?.page || 1);
        const issueCount = nextCount > 0 ? getIssueCountForPage(values || {}, page) : 0;
        sectionData?.updateIssueCount?.(issueCount);
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [allValues, currentSection?.formId, getIssueCountForPage, sectionData?.nextCount, sectionData?.page, sectionData?.updateIssueCount, values]);

    function isSelected(itemId) {
        return Number(values?.[currentSection.formId + itemId] || 0) === 1;
    }

    const onSelectItem = useCallback(function (item) {
        const itemId = Number(item?.id || 0);
        if (!itemId) return;
        const nextValue = Number(values?.[currentSection.formId + itemId] || 0) === 1 ? 0 : 1;
        setValue(itemId, nextValue);
    }, [currentSection?.formId, setValue, values]);

    function getSelectedCountForBox(boxNumber) {
        const itemsInBox = pageValues.filter((m) => getBoxNumber(m) === Number(boxNumber));
        return itemsInBox.reduce((count, m) => count + (isSelected(m.id) ? 1 : 0), 0);
    }

    useEffect(() => {
        if (!sectionData?.isAdmin) return;

        const handleKeyDown = (e: KeyboardEvent) => {
            const activeTag = document.activeElement?.tagName?.toLowerCase();
            if (activeTag === "input" || activeTag === "textarea" || activeTag === "select") return;

            if (e.key === "Enter") {
                e.preventDefault();
                const currentPage = Number(sectionData?.page || 1);
                const totalPages = allValues.length > 0
                    ? Math.max(...allValues.map(m => Number((m as { page?: string | number })?.page || 1)))
                    : 1;
                if (currentPage < totalPages && sectionData?.onNextPage)
                    sectionData.onNextPage();
                return;
            }

            const itemIndex = Number(e.key);
            if (!Number.isInteger(itemIndex) || itemIndex < 1 || itemIndex > 6) return;

            // Pick the first box that is still unanswered (no selected option yet).
            const firstUnansweredBox = [1, 2, 3, 4].find((boxNumber) => {
                const itemsInBox = pageValues.filter((m) => getBoxNumber(m) === boxNumber);
                if (itemsInBox.length === 0) return false;
                return itemsInBox.every((item) => Number(values?.[currentSection.formId + item.id] || 0) === 0);
            });

            if (!firstUnansweredBox) return;

            const itemsInBox = pageValues.filter((m) => getBoxNumber(m) === firstUnansweredBox);
            const selectedItem = itemsInBox[itemIndex - 1];
            if (!selectedItem) return;

            e.preventDefault();
            onSelectItem(selectedItem);
        };

        document.addEventListener("keydown", handleKeyDown);
        return () => document.removeEventListener("keydown", handleKeyDown);
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [allValues, currentSection?.formId, onSelectItem, pageValues, sectionData?.isAdmin, sectionData?.onNextPage, sectionData?.page, setValue, values]);

    return (
        <>
            <div className="instructions">
                {parseSanitizedHtml((page > 1 ? reminderHtml : instructionsHtml) || "")}
            </div>
            <div className="boxes">
            {Array.from({ length: 4 }, (_, i) => {
                const boxNumber = i + 1;
                const isInvalidBox = sectionData?.showValidations && getSelectedCountForBox(boxNumber) < 1;

                return (
                <div className="box" key={i}>
                    <div className="boxLabel">Box {boxNumber}</div>
                    <ul>
                        {pageValues.filter(m => getBoxNumber(m) === boxNumber).map((m, j) => (
                            <li
                                className={`checkbox${isInvalidBox ? " invalid" : ""}`}
                                key={m.id || j}
                                onClick={() => onSelectItem(m)}
                            >
                                <span className={`radio${isSelected(m.id) ? " checked" : ""}`} aria-hidden="true">
                                    {isSelected(m.id)
                                        ? <FaCircle className="actionIcon" />
                                        : <FaRegCircle className="actionIcon" />}
                                </span>
                                <span>{m.description || m.name}</span>
                            </li>
                        ))}
                    </ul>
                </div>
            )})}
            </div>


        </>
    );
}



